'use client';

/* eslint-disable @typescript-eslint/no-unused-vars */
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  createContext,
  ReactNode,
  useEffect,
  useState,
} from 'react';
// Removed haversine import - backend now handles distance calculations
// Removed searchRetailStoresAPI import - using direct fetch calls now

export type ClusterElementType = {
  clusterPoints: Array<StoreElementType>;
  count: number;
  lat: string | number;
  lng: string | number;
};

export type StoreElementType = {
  retailPartnerName: string;
  country: string;
  iso: string;
  city: string;
  lat: string | number;
  long: string | number;
  storeOpenTime: string;
  storeClosingTime: string;
  address: string;
  gMapUrl?: string;
  storeTimings: string | null;
  contact: any;
  retailWebsiteLink?: string;
  inStore: boolean;
};

export const SEARCH_INPUT_MIN_CHAR_LENGTH = 3;

export enum ScreenState {
  HOME = 'home',
  SEARCH = 'search',
  EASTEREGG = 'easteregg',
}

type ResultType = null | 'search' | 'userLocation' | 'cluster';

type FindOnGlobeContextType = {
  allStores: null | Record<string, Array<StoreElementType>>;
  resultType: ResultType;
  setResultType: (i: ResultType) => void;
  loading: boolean;
  search: (query: string) => void;
  setSearchResult: (result: ReadonlyArray<StoreElementType>) => void;
  setSearchResultWithoutTypeChange: (
    result: ReadonlyArray<StoreElementType>,
  ) => void;
  searchQuery: null | string;
  clearSearch: () => void;
  searchResult: ReadonlyArray<StoreElementType>;
  clusterCollection: ReadonlyArray<ClusterElementType>;
  setClusterCollection: (cluster: Array<ClusterElementType>) => void;
  selectedCluster: null | ClusterElementType;
  setSelectedCluster: (cluster: ClusterElementType | null) => void;
  selectedStore: StoreElementType | null;
  setSelectedStore: (store: StoreElementType | null) => void;
  clearSelectedStore: () => void;
  populateUserLocationStores: () => void;
  // userLocationStoresResult: ReadonlyArray<StoreElementType>;
  userLocation: null | GeolocationPosition;
  userCountry: {
    country: undefined | string;
    countryName: string | null | undefined;
  };
  searchControlVisible: boolean;
  setSearchControlVisible: (visible: boolean) => void;
  screenState: ScreenState;
  setScreenState: (state: ScreenState) => void;
  storesLoaded: boolean;
};

const initialFindOnGlobeContextValue: FindOnGlobeContextType = {
  allStores: null,
  resultType: null,
  setResultType: () => undefined,
  loading: false,
  search: () => undefined,
  setSearchResult: () => undefined,
  setSearchResultWithoutTypeChange: () => undefined,
  searchQuery: null,
  clearSearch: () => undefined,
  searchResult: [],
  selectedCluster: null,
  clusterCollection: [],
  setClusterCollection: () => undefined,
  selectedStore: null,
  setSelectedStore: () => undefined,
  setSelectedCluster: () => undefined,
  clearSelectedStore: () => undefined,
  populateUserLocationStores: () => undefined,
  // userLocationStoresResult: [],
  userLocation: null,
  userCountry: {
    country: undefined,
    countryName: undefined,
  },
  searchControlVisible: true,
  setSearchControlVisible: () => undefined,
  screenState: ScreenState.HOME,
  setScreenState: () => undefined,
  storesLoaded: false,
};

export const FindOnGlobeContext = createContext<FindOnGlobeContextType>(
  initialFindOnGlobeContextValue,
);

export const FindOnGlobeProvider = ({ children }: { children: ReactNode }) => {
  const { region: country, countryName } = useRegionLocale();

  const [allStores, setAllStores] = useState<Record<
    string,
    Array<StoreElementType>
  > | null>(null);
  const [allStoresSingleList, setAllStoresSingleList] = useState<
    Array<StoreElementType>
  >([]);
  const [storesLoaded, setStoresLoaded] = useState(false);

  useEffect(() => {
    // Load all stores from API
    fetch('/api/stores/countries')
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch countries: ${res.status}`);
        }
        return res.json();
      })
      .then(
        async (countriesData: {
          countries: Array<{
            iso: string;
            country: string;
            storeCount: number;
          }>;
          totalCountries: number;
        }) => {
          const countryCodes = countriesData.countries.map(
            (country) => country.iso,
          );
          const storePromises = countryCodes.map(async (country) => {
            const response = await fetch(`/api/stores/countries/${country}`);
            if (!response.ok) {
              throw new Error(
                `Failed to fetch stores for ${country}: ${response.status}`,
              );
            }
            const stores = await response.json();
            return { [country]: stores };
          });

          const storeResults = await Promise.all(storePromises);
          const allStoresData = storeResults.reduce(
            (acc, curr) => ({ ...acc, ...curr }),
            {},
          );

          setAllStores(allStoresData);
          setAllStoresSingleList(Object.values(allStoresData).flat());
          setStoresLoaded(true);
        },
      )
      .catch((error) => {
        console.error('Error loading stores:', error);
        setStoresLoaded(true);
        // Set empty data on error
        setAllStores({});
        setAllStoresSingleList([]);
      });
  }, []);

  const [loading, setLoading] = useState(false);
  const [resultType, setResultType] = useState<ResultType>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [searchResult, setSearchResult_state] = useState<
    ReadonlyArray<StoreElementType>
  >([]);
  const setSearchResult = (result: ReadonlyArray<StoreElementType>) => {
    // Backend now handles sorting, so we can use the result directly
    setSearchResult_state(result);
    setResultType('search');
  };

  // Separate function for setting search results without overriding result type
  const setSearchResultWithoutTypeChange = (
    result: ReadonlyArray<StoreElementType>,
  ) => {
    // Backend now handles sorting, so we can use the result directly
    setSearchResult_state(result);
  };
  const [searchControlVisible, setSearchControlVisible] = useState(true);
  const [selectedStore, setSelectedStore_state] =
    useState<StoreElementType | null>(null);
  const setSelectedStore = (store: StoreElementType | null) => {
    if (store) setResultType('search');
    setSelectedStore_state(store);
  };

  const [selectedCluster, setSelectedCluster_state] =
    useState<ClusterElementType | null>(null);
  const [clusterCollection, setClusterCollection_state] = useState<
    Array<ClusterElementType>
  >([]);
  // const [userLocationStoresResult, setUserLocationStoresResult] = useState<
  //   ReadonlyArray<StoreElementType>
  // >([]);
  const setSelectedCluster = (cluster: ClusterElementType | null) => {
    setSelectedCluster_state(cluster);
    if (cluster && cluster.count > 0) setResultType('cluster');
  };
  const setClusterCollection = (
    clusterCollection: Array<ClusterElementType>,
  ) => {
    setClusterCollection_state(clusterCollection);
  };
  const [userLocation, setUserLocation] = useState<GeolocationPosition | null>(
    null,
  );

  const userCountry = { country, countryName };

  const [screenState, setScreenState] = useState<ScreenState>(ScreenState.HOME);

  const search = (query: string) => {
    if (query.length >= SEARCH_INPUT_MIN_CHAR_LENGTH) {
      setLoading(true);
      setSearchQuery(query);
      setResultType('search');
    }
  };

  const clearSearch = () => {
    setSearchQuery(null);
    setSearchResult([]);
    setResultType(null);
  };

  const clearSelectedStore = () => {
    setSelectedStore(null);
  };

  const populateUserLocationStores_CountryDetected = () => {
    if (!userCountry?.country || !allStores) return;
    setSearchResultWithoutTypeChange([]);
    const stores =
      allStores[userCountry.country as keyof typeof allStores] || [];
    if (stores?.length > 0) {
      setSearchResultWithoutTypeChange(stores);
      setResultType('userLocation');
    }
  };

  const populateUserLocationStores_LocationDetected = async () => {
    if (!userLocation || !userCountry?.country || !allStores) {
      return;
    }

    const currentTime = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const storesForCountry =
      allStores[userCountry.country as keyof typeof allStores];

    if (!storesForCountry || storesForCountry.length === 0) {
      console.log('No stores found for country:', userCountry.country);
      // Try to find stores in nearby countries or show all stores
      const allStoresArr = Object.values(allStores).flat();
      if (allStoresArr.length > 0) {
        console.log('Showing all stores as fallback');
        setSearchResultWithoutTypeChange(allStoresArr);
        setResultType('userLocation');
        console.log(
          'Set resultType to userLocation (fallback) with',
          allStoresArr.length,
          'stores',
        );
      }
      return;
    }

    // Backend now handles sorting when user location is provided
    // We need to fetch stores with location parameters
    const url = `/api/stores/countries/${userCountry.country}?lat=${userLocation.coords.latitude}&lng=${userLocation.coords.longitude}`;

    try {
      const response = await fetch(url);
      if (response.ok) {
        const responseData = await response.json();
        const data = responseData.data || responseData;
        const sortedStores = data.stores || [];

        console.log(
          'Found stores for country:',
          userCountry.country,
          'count:',
          sortedStores.length,
        );
        setSearchResultWithoutTypeChange(sortedStores);
      } else {
        console.error('Failed to fetch sorted stores');
        setSearchResultWithoutTypeChange(storesForCountry);
      }
    } catch (error) {
      console.error('Error fetching sorted stores:', error);
      setSearchResultWithoutTypeChange(storesForCountry);
    }
    setResultType('userLocation');
    console.log('Set resultType to userLocation');
  };

  const populateUserLocationStores = () => {
    console.log('populateUserLocationStores called');
    if (!window || !window.navigator) {
      console.log('No window or navigator available');
      return;
    }
    clearSearch();
    setLoading(true);

    const minsToConsider = 15;

    if (
      userLocation &&
      // userLocation.timestamp > refTimestamp &&
      new Date().getTime() - userLocation.timestamp <
        1000 * 60 * minsToConsider &&
      userLocation.coords
    ) {
      console.log('Using cached userLocation');
      populateUserLocationStores_LocationDetected();
      setResultType('userLocation');
      return;
    }

    console.log('Requesting new location...');
    window.navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        console.log('Location obtained:', position);
        setUserLocation(position);
        setResultType('userLocation');
      },
      (error: GeolocationPositionError) => {
        console.error('Geolocation error:', error);
        populateUserLocationStores_CountryDetected();
        setResultType('userLocation');
      },
      {
        timeout: 10000,
      },
    );
  };

  const values = {
    allStores,
    resultType,
    setResultType,
    loading,
    search,
    searchQuery,
    clearSearch,
    searchResult,
    setSearchResult,
    setSearchResultWithoutTypeChange,
    selectedStore,
    setSelectedStore,
    clearSelectedStore,
    clusterCollection,
    setClusterCollection,
    selectedCluster,
    setSelectedCluster,
    populateUserLocationStores,
    // userLocationStoresResult,
    userLocation,
    userCountry,
    searchControlVisible,
    setSearchControlVisible,
    screenState,
    setScreenState,
    storesLoaded,
  };

  // useEffect(() => {
  //   if (country && countryName && country !== userCountry?.country) {
  //     setUserCountry({ country, countryName });
  //   }
  // }, [country]);

  useEffect(() => {
    populateUserLocationStores_LocationDetected();
  }, [userLocation]);

  // Add new useEffect to handle the case when allStores are loaded after userLocation
  useEffect(() => {
    if (allStores && userLocation && userCountry?.country) {
      populateUserLocationStores_LocationDetected();
    }
  }, [allStores, userLocation, userCountry?.country]);

  useEffect(() => {
    if (!searchQuery) return;

    const performSearch = async () => {
      setLoading(true);

      try {
        // Try API search first if we have a country
        if (userCountry?.country) {
          let url = `/api/stores/countries/${
            userCountry.country
          }/search?q=${encodeURIComponent(searchQuery)}`;
          if (userLocation) {
            url += `&lat=${userLocation.coords.latitude}&lng=${userLocation.coords.longitude}`;
          }

          const response = await fetch(url);
          if (response.ok) {
            const responseData = await response.json();
            const data = responseData.data || responseData;
            const apiResults = data.stores || [];
            if (apiResults.length > 0) {
              setSearchResult(apiResults);
              setLoading(false);
              return;
            }
          }
        }

        // Fallback to local search
        if (allStoresSingleList.length > 0) {
          const lowerCaseSearchStr = searchQuery.toLowerCase();
          const filteredItems = allStoresSingleList.filter((store) =>
            [
              store.retailPartnerName,
              store.country,
              store.city,
              store.address,
            ].some((field) => field.toLowerCase().includes(lowerCaseSearchStr)),
          );
          setSearchResult(filteredItems);
        } else {
          setSearchResult([]);
        }
      } catch (error) {
        console.error('Search error:', error);
        // Fallback to local search on error
        if (allStoresSingleList.length > 0) {
          const lowerCaseSearchStr = searchQuery.toLowerCase();
          const filteredItems = allStoresSingleList.filter((store) =>
            [
              store.retailPartnerName,
              store.country,
              store.city,
              store.address,
            ].some((field) => field.toLowerCase().includes(lowerCaseSearchStr)),
          );
          setSearchResult(filteredItems);
        } else {
          setSearchResult([]);
        }
      }

      setLoading(false);
    };

    performSearch();
  }, [searchQuery, userCountry?.country, allStoresSingleList]);

  useEffect(() => {
    if (resultType) setLoading(false);
  }, [searchResult, resultType]);

  // useEffect(() => {
  //   if (selectedCluster && selectedCluster.clusterPoints.length > 1) {
  //     setResultType('search');
  //     setSearchResult(selectedCluster.clusterPoints);
  //   }
  // }, [selectedCluster]);

  useEffect(() => {
    if (window) (window as any)['fgc'] = values;
  }, [...Object.values(values)]);

  return (
    <FindOnGlobeContext.Provider value={values}>
      {children}
      {/* <button
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '50%',
          padding: '10px 24px',
          borderRadius: '40px',
          backgroundColor: '#ffffff',
          color: '#000000',
          zIndex: 10000,
          fontWeight: '500',
          transform: 'translateX(-50%)',
        }}
        onClick={() => {
          setScreenState(
            screenState === ScreenState.HOME
              ? ScreenState.EASTEREGG
              : ScreenState.HOME,
          );
        }}
      >
        Toggle UI
      </button> */}
    </FindOnGlobeContext.Provider>
  );
};
